import { useEffect, useMemo, useState } from 'react';
import {
  getAntisemitismTypes,
  getSubjects,
  getInteractions,
  getLandingPageStatistics,
} from '../api/landing_api';
import { getEntities } from '../api/entities_api';

/** Survives Strict Mode remounts; dedupes in-flight load across effect runs. */
let landingDataModuleCache = null;
let landingDataLoadPromise = null;

async function loadLandingPageDataOnce() {
  if (landingDataModuleCache) {
    return landingDataModuleCache;
  }
  if (landingDataLoadPromise) {
    return landingDataLoadPromise;
  }

  landingDataLoadPromise = (async () => {
    const [
      ihraData,
      subjectsData,
      recentEntitiesResult,
      interactions,
      defaultStatistics,
    ] = await Promise.all([
      getAntisemitismTypes().catch(() => null),
      getSubjects().catch(() => null),
      getEntities({ limit: 5, offset: 0, columns: ['AntisemitismTypes', 'Language'] }).catch(() => null),
      getInteractions()
        .then((data) => (data && typeof data === 'object' ? data : null))
        .catch(() => null),
      getLandingPageStatistics({}).catch(() => null),
    ]);

    const recentEntities =
      recentEntitiesResult?.entities ||
      recentEntitiesResult?.data ||
      (Array.isArray(recentEntitiesResult) ? recentEntitiesResult : []);

    landingDataModuleCache = {
      ihraData,
      subjectsData,
      recentEntities,
      interactions,
      defaultStatistics,
    };
    return landingDataModuleCache;
  })();

  try {
    return await landingDataLoadPromise;
  } finally {
    landingDataLoadPromise = null;
  }
}

const initialLoading = {
  ihra: true,
  subjects: true,
  recentEntities: true,
  interactions: true,
  defaultStatistics: true,
};

/**
 * Loads landing page API payloads once (cached in module scope) on first visit to `/` after init.
 * @param {{ isInitialized: boolean, pathname: string }} options
 */
export function useLandingPageData({ isInitialized, pathname }) {
  const [ihraData, setIhraData] = useState(null);
  const [subjectsData, setSubjectsData] = useState(null);
  const [recentEntities, setRecentEntities] = useState([]);
  const [interactions, setInteractions] = useState(null);
  const [defaultStatistics, setDefaultStatistics] = useState(null);
  const [loading, setLoading] = useState(initialLoading);

  useEffect(() => {
    if (!isInitialized || pathname !== '/') return;

    let cancelled = false;

    if (landingDataModuleCache) {
      const c = landingDataModuleCache;
      setIhraData(c.ihraData);
      setSubjectsData(c.subjectsData);
      setRecentEntities(c.recentEntities);
      setInteractions(c.interactions);
      setDefaultStatistics(c.defaultStatistics);
      setLoading({
        ihra: false,
        subjects: false,
        recentEntities: false,
        interactions: false,
        defaultStatistics: false,
      });
      return undefined;
    }

    setLoading({ ...initialLoading });

    loadLandingPageDataOnce()
      .then((data) => {
        if (cancelled) return;
        setIhraData(data.ihraData);
        setSubjectsData(data.subjectsData);
        setRecentEntities(data.recentEntities);
        setInteractions(data.interactions);
        setDefaultStatistics(data.defaultStatistics);
        setLoading({
          ihra: false,
          subjects: false,
          recentEntities: false,
          interactions: false,
          defaultStatistics: false,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setLoading({
          ihra: false,
          subjects: false,
          recentEntities: false,
          interactions: false,
          defaultStatistics: false,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [isInitialized, pathname]);

  return useMemo(
    () => ({
      ihraData,
      subjectsData,
      recentEntities,
      interactions,
      defaultStatistics,
      loading,
    }),
    [ihraData, subjectsData, recentEntities, interactions, defaultStatistics, loading]
  );
}
